import { useEffect, useRef, useState } from "react";
import posthog from "posthog-js";
import { GROUNDY } from "@/components/onboarding/groundyArt";
import { OButton, OC, ScreenFrame, StepHeader, StepHeading, StepLine } from "@/components/onboarding/onboardingUi";
import { PickCard } from "@/components/onboarding/PickCard";
import { useSuggestedPicks, type SuggestedPick } from "@/hooks/useSuggestedPicks";
import type { SuggestionGeo } from "@/lib/suggestedCafeRanking";
import { getPendingPicks, togglePendingPick } from "@/lib/onboardingPending";

export const PICKS_EMPTY_COPY = "You can save cafés anytime from Browse.";
/** "Not now" on location: the picks come from Southern California (see rankSuggestedCafes). */
export const PICKS_NO_PLACE_LINE = "A few of the highest scored cafés in Southern California to get you started. Tap the heart to save.";

export function PicksStep({
  geo,
  priorities,
  onContinue,
  onSkip,
}: {
  geo: SuggestionGeo | null;
  priorities: string[];
  onContinue: () => void;
  onSkip: () => void;
}) {
  // The 3 cards and their grades come from one answer of the picks function.
  const { data, isLoading, isError } = useSuggestedPicks({ geo, priorities });
  const suggestions = data ?? [];
  const [savedIds, setSavedIds] = useState<string[]>(() => getPendingPicks().map((p) => p.id));

  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    try { posthog.capture("onboarding_first_save_shown"); } catch { /* ignore */ }
  }, []);

  const toggle = (cafe: SuggestedPick) => {
    const nowSaved = togglePendingPick({
      id: cafe.id,
      name: cafe.name,
      area: cafe.area,
      score: cafe.work_readiness_score ?? null,
      grades: cafe.grades,
      forYou: cafe.for_you,
    });
    setSavedIds(getPendingPicks().map((p) => p.id));
    if (nowSaved) {
      try { posthog.capture("onboarding_first_save", { cafe_id: cafe.id }); } catch { /* ignore */ }
    }
  };

  const hasSaved = savedIds.length > 0;

  return (
    <ScreenFrame
      testId="picks-step"
      dock={
        hasSaved ? (
          <OButton onClick={onContinue}>Continue →</OButton>
        ) : (
          <OButton kind="outline" onClick={onSkip}>Skip for now</OButton>
        )
      }
    >
      <StepHeader groundy={GROUNDY.picks} eyebrow="Your picks" />
      <StepHeading>Save your first café</StepHeading>
      <StepLine>
        {geo
          ? "A few spots near you to get you started. Tap the heart to save."
          : PICKS_NO_PLACE_LINE}
      </StepLine>
      <div className="flex flex-col gap-3 mt-5">
        {isLoading && (
          <p className="text-center py-8" style={{ fontSize: 13, color: OC.muted }}>Finding cafés…</p>
        )}
        {!isLoading && (isError || suggestions.length === 0) && (
          <p className="text-center py-8" style={{ fontSize: 14, color: OC.textSec }} data-testid="picks-empty">
            {PICKS_EMPTY_COPY}
          </p>
        )}
        {suggestions.map((cafe) => (
          <PickCard
            key={cafe.id}
            name={cafe.name}
            area={cafe.area}
            score={cafe.work_readiness_score ?? null}
            grades={cafe.grades}
            forYou={cafe.for_you}
            saved={savedIds.includes(cafe.id)}
            onToggle={() => toggle(cafe)}
          />
        ))}
      </div>
    </ScreenFrame>
  );
}
